import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send,
  MessageSquare,
  FileText,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  X,
  Plus,
  BookOpen,
  Columns,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  chatWithContracts,
  createChatConversation,
  updateChatConversation,
  type ChatResponseFromAPI,
  type ContractChatResponseFromAPI,
  type CitationFromAPI,
  type PersistedChatMessage,
  type ConversationSummary,
} from '../../services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatContractOption {
  id: string;
  name: string;
  state: string;
  status: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  responses?: ContractChatResponseFromAPI[];
  comparisonAnalysis?: string;
  timestamp: Date;
}

export interface ContractChatProps {
  contracts: ChatContractOption[];
  onNavigate?: (path: string) => void;
  conversationId?: string | null;
  initialMessages?: ChatMessage[];
  initialContractIds?: string[];
  initialDualMode?: boolean;
  onConversationCreated?: (conv: ConversationSummary) => void;
  onMessagesUpdated?: (conversationId: string, messages: PersistedChatMessage[]) => void;
  onNewChat?: () => void;
}

// ---------------------------------------------------------------------------
// Citation card sub-component
// ---------------------------------------------------------------------------

const CitationCard: React.FC<{ citation: CitationFromAPI; index: number }> = ({ citation, index }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="border border-secondary-200 rounded-md bg-white overflow-hidden"
      data-testid={`citation-${index}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary-50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary-100 text-primary-700 text-xs font-bold flex-shrink-0">
            {index + 1}
          </span>
          <span className="font-medium text-secondary-700 truncate">
            {citation.sectionPath ?? `Article ${citation.articleNumber ?? '?'}`}
          </span>
          {/* similarity score hidden from UI but still available in data */}
        </span>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-secondary-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-secondary-400 flex-shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-3 border-t border-secondary-100">
          <p className="text-sm text-secondary-600 mt-2 whitespace-pre-wrap leading-relaxed">
            {citation.text}
          </p>
          {citation.page != null && (
            <p className="text-xs text-secondary-400 mt-1">Page {citation.page}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Answer pane for a single contract (used in both single and dual mode)
// ---------------------------------------------------------------------------

const chatMarkdownComponents = {
  h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <h4 className="text-sm font-bold text-secondary-900 mt-3 mb-1" {...props}>{children}</h4>,
  h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <h5 className="text-sm font-semibold text-secondary-900 mt-3 mb-1" {...props}>{children}</h5>,
  h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <h6 className="text-sm font-semibold text-secondary-800 mt-2 mb-1" {...props}>{children}</h6>,
  h4: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => <h6 className="text-sm font-medium text-secondary-800 mt-2 mb-1" {...props}>{children}</h6>,
};

const AnswerPane: React.FC<{
  response: ContractChatResponseFromAPI;
  isLoading?: boolean;
}> = ({ response, isLoading }) => {
  return (
    <div className="flex flex-col h-full">
      {/* Contract header */}
      <div className="flex items-center gap-2 px-4 py-2 bg-secondary-50 border-b border-secondary-200">
        <FileText className="h-4 w-4 text-primary-600" />
        <span className="text-sm font-semibold text-secondary-800 truncate">
          {response.contractName ?? response.contractId}
        </span>
      </div>

      {/* Answer body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-secondary-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Analyzing contract...</span>
          </div>
        ) : (
          <>
            <div className="prose prose-sm max-w-none text-secondary-800">
              <ReactMarkdown components={chatMarkdownComponents}>{response.answer}</ReactMarkdown>
            </div>

            {/* Citations section */}
            {response.citations.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <BookOpen className="h-3 w-3" />
                  Sources ({response.citations.length})
                </h4>
                <div className="space-y-1.5">
                  {response.citations.map((citation, i) => (
                    <CitationCard key={citation.chunkId} citation={citation} index={i} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Comparison analysis pane (displayed below side-by-side answers)
// ---------------------------------------------------------------------------

const ComparisonPane: React.FC<{ analysis: string }> = ({ analysis }) => {
  return (
    <div className="mt-3 bg-amber-50 rounded-lg border border-amber-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-100 border-b border-amber-200">
        <Columns className="h-4 w-4 text-amber-700" />
        <span className="text-sm font-semibold text-amber-800">Key Differences</span>
      </div>
      <div className="px-4 py-3">
        <div className="prose prose-sm max-w-none text-secondary-800">
          <ReactMarkdown components={chatMarkdownComponents}>{analysis}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Contract selector dropdown
// ---------------------------------------------------------------------------

const ContractDropdown: React.FC<{
  label: string;
  contracts: ChatContractOption[];
  selectedId: string;
  onChange: (id: string) => void;
  excludeId?: string;
  disabled?: boolean;
}> = ({ label, contracts, selectedId, onChange, excludeId, disabled }) => {
  const processedStatuses = ['structured', 'embedded', 'linked', 'ready'];
  const available = contracts.filter(
    (c) => processedStatuses.includes(c.status) && c.id !== excludeId
  );

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-secondary-500">{label}</label>
      <select
        value={selectedId}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="block w-full rounded-md border-secondary-300 bg-white py-1.5 px-3 text-sm text-secondary-800 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50"
        data-testid={`contract-select-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <option value="">Select a contract</option>
        {available.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} ({c.state})
          </option>
        ))}
      </select>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main ContractChat component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Helpers for persistence
// ---------------------------------------------------------------------------

function messagesToPersisted(msgs: ChatMessage[]): PersistedChatMessage[] {
  return msgs.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    responses: m.responses,
    comparisonAnalysis: m.comparisonAnalysis,
    timestamp: m.timestamp.toISOString(),
  }));
}

function autoTitle(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= 80) return trimmed;
  const truncated = trimmed.slice(0, 80);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 40 ? truncated.slice(0, lastSpace) : truncated) + '...';
}

// ---------------------------------------------------------------------------
// Main ContractChat component
// ---------------------------------------------------------------------------

export const ContractChat: React.FC<ContractChatProps> = ({
  contracts,
  onNavigate,
  conversationId,
  initialMessages,
  initialContractIds,
  initialDualMode,
  onConversationCreated,
  onMessagesUpdated,
  onNewChat,
}) => {
  const [contractId1, setContractId1] = useState(initialContractIds?.[0] ?? '');
  const [contractId2, setContractId2] = useState(initialContractIds?.[1] ?? '');
  const [dualMode, setDualMode] = useState(initialDualMode ?? false);
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages ?? []);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastMessageRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const activeConvIdRef = useRef<string | null | undefined>(conversationId);

  // Sync internal state when the parent changes the active conversation
  useEffect(() => {
    activeConvIdRef.current = conversationId;
    if (initialMessages) {
      setMessages(initialMessages);
    } else {
      setMessages([]);
    }
    setContractId1(initialContractIds?.[0] ?? '');
    setContractId2(initialContractIds?.[1] ?? '');
    setDualMode(initialDualMode ?? false);
    setError(null);
    setInputValue('');
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to the top of the newest message so the answer is visible
  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [messages]);

  // Build conversation history for API calls (exclude system messages)
  const buildHistory = useCallback(() => {
    return messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({
        role: m.role,
        content: m.role === 'assistant' && m.responses
          ? m.responses.map((r) => r.answer).join('\n\n')
            + (m.comparisonAnalysis ? '\n\nKey Differences:\n' + m.comparisonAnalysis : '')
          : m.content,
      }));
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const contractIds = [contractId1, ...(dualMode && contractId2 ? [contractId2] : [])].filter(Boolean);
    if (contractIds.length === 0) {
      setError('Please select at least one contract.');
      return;
    }

    setError(null);
    setInputValue('');

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const history = buildHistory();
      const result: ChatResponseFromAPI = await chatWithContracts({
        message: text,
        contractIds,
        conversationHistory: history,
      });

      if (!result.success) {
        throw new Error(result.error ?? 'Chat request failed.');
      }

      const assistantMsg: ChatMessage = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: result.responses.map((r) => r.answer).join('\n\n---\n\n'),
        responses: result.responses,
        comparisonAnalysis: result.comparisonAnalysis,
        timestamp: new Date(),
      };

      const updatedMessages = [...messages, userMsg, assistantMsg];
      setMessages(updatedMessages);

      // Persist conversation (fire-and-forget)
      const persisted = messagesToPersisted(updatedMessages);
      if (activeConvIdRef.current) {
        // Update existing conversation
        updateChatConversation(activeConvIdRef.current, { messages: persisted }).catch((e) =>
          console.error('[chat] Failed to update conversation:', e),
        );
        onMessagesUpdated?.(activeConvIdRef.current, persisted);
      } else {
        // Create new conversation
        const title = autoTitle(text);
        createChatConversation({
          title,
          contractIds,
          dualMode,
          messages: persisted,
        })
          .then((conv) => {
            activeConvIdRef.current = conv.id;
            onConversationCreated?.({
              id: conv.id,
              title: conv.title,
              contractIds: conv.contractIds,
              dualMode: conv.dualMode,
              createdAt: conv.createdAt,
              updatedAt: conv.updatedAt,
            });
          })
          .catch((e) => console.error('[chat] Failed to create conversation:', e));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred.');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, contractId1, contractId2, dualMode, buildHistory, messages, onConversationCreated, onMessagesUpdated]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      setMessages([]);
      setError(null);
    }
  };

  const hasContracts = contractId1 || (dualMode && contractId2);

  return (
    <div className="flex flex-col h-full" data-testid="contract-chat">
      {/* Header bar */}
      <div className="border-b border-secondary-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary-600" />
            <h1 className="text-lg font-semibold text-secondary-900">Contract Chat</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setDualMode(!dualMode);
                if (!dualMode) setContractId2('');
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                dualMode
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-secondary-100 text-secondary-600 border border-secondary-300 hover:bg-secondary-200'
              }`}
              data-testid="toggle-dual-mode"
            >
              <Columns className="h-4 w-4" />
              {dualMode ? 'Compare Mode' : 'Single Mode'}
            </button>
            {messages.length > 0 && (
              <button
                onClick={handleClearChat}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100"
                data-testid="clear-chat"
              >
                <Plus className="h-4 w-4" />
                New Chat
              </button>
            )}
          </div>
        </div>

        {/* Contract selectors */}
        <div className={`grid gap-3 ${dualMode ? 'grid-cols-2' : 'grid-cols-1 max-w-md'}`}>
          <ContractDropdown
            label="Contract 1"
            contracts={contracts}
            selectedId={contractId1}
            onChange={(id) => {
              setContractId1(id);
              setMessages([]);
            }}
            excludeId={dualMode ? contractId2 : undefined}
            disabled={isLoading}
          />
          {dualMode && (
            <ContractDropdown
              label="Contract 2"
              contracts={contracts}
              selectedId={contractId2}
              onChange={(id) => {
                setContractId2(id);
                setMessages([]);
              }}
              excludeId={contractId1}
              disabled={isLoading}
            />
          )}
        </div>
      </div>

      {/* Message area */}
      <div className="flex-1 overflow-y-auto bg-secondary-50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-secondary-400 px-6">
            <MessageSquare className="h-12 w-12 mb-3 opacity-40" />
            <p className="text-lg font-medium mb-1">Ask a question about {dualMode ? 'these contracts' : 'this contract'}</p>
            <p className="text-sm text-center max-w-md">
              {hasContracts
                ? 'Type your question below. Answers will include citations to specific contract articles.'
                : 'Select a contract above to begin.'}
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
              {[
                'What are the grievance and appeal procedures?',
                'Summarize the network adequacy requirements.',
                'What are the quality reporting obligations?',
                'Describe the covered benefits and exclusions.',
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    if (hasContracts) {
                      setInputValue(q);
                      inputRef.current?.focus();
                    }
                  }}
                  disabled={!hasContracts}
                  className="text-left text-xs px-3 py-2 rounded-md border border-secondary-200 bg-white text-secondary-600 hover:border-primary-300 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={msg.id}
                ref={index === messages.length - 1 ? lastMessageRef : undefined}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'user' ? (
                  <div className="max-w-[70%] bg-primary-600 text-white rounded-lg px-4 py-2.5 shadow-sm">
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ) : msg.responses ? (
                  /* Assistant response with contract panes */
                  <div className="w-full">
                    {msg.responses.length === 1 ? (
                      /* Single contract answer */
                      <div className="bg-white rounded-lg border border-secondary-200 shadow-sm overflow-hidden">
                        <AnswerPane response={msg.responses[0]} />
                      </div>
                    ) : (
                      /* Dual contract side-by-side */
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          {msg.responses.map((resp) => (
                            <div
                              key={resp.contractId}
                              className="bg-white rounded-lg border border-secondary-200 shadow-sm overflow-hidden"
                            >
                              <AnswerPane response={resp} />
                            </div>
                          ))}
                        </div>
                        {msg.comparisonAnalysis && (
                          <ComparisonPane analysis={msg.comparisonAnalysis} />
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  /* Plain text fallback */
                  <div className="max-w-[70%] bg-white rounded-lg border border-secondary-200 px-4 py-2.5 shadow-sm">
                    <p className="text-sm text-secondary-800 whitespace-pre-wrap">{msg.content}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className={`w-full ${dualMode ? 'grid grid-cols-2 gap-3' : ''}`}>
                  {(dualMode ? [contractId1, contractId2] : [contractId1]).filter(Boolean).map((cId) => {
                    const contract = contracts.find((c) => c.id === cId);
                    return (
                      <div
                        key={cId}
                        className="bg-white rounded-lg border border-secondary-200 shadow-sm overflow-hidden"
                      >
                        <AnswerPane
                          response={{
                            contractId: cId,
                            contractName: contract?.name,
                            answer: '',
                            citations: [],
                          }}
                          isLoading
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Input bar */}
      <div className="border-t border-secondary-200 bg-white px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              hasContracts
                ? 'Ask a question about the contract...'
                : 'Select a contract above to start chatting'
            }
            disabled={!hasContracts || isLoading}
            rows={1}
            className="flex-1 resize-none rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-800 placeholder-secondary-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 disabled:opacity-50 disabled:bg-secondary-50"
            style={{ minHeight: '38px', maxHeight: '120px' }}
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || !hasContracts || isLoading}
            className="inline-flex items-center justify-center rounded-lg bg-primary-600 p-2.5 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="chat-send"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractChat;
